import { useAuth } from './useAuth';

/**
 * Beta features in staged rollout, keyed as the backend's `RolloutFlag` serializes
 * them. Delete a key when its flag is retired — `tsc` then finds every gate on it.
 */
export const ROLLOUTS = {
  segmentationMatrix: 'segmentation_matrix',
  psaTicketing: 'psa_ticketing',
} as const;

export type RolloutFlag = (typeof ROLLOUTS)[keyof typeof ROLLOUTS];

/**
 * Whether a beta feature is rolled out to the current organization.
 *
 * The fourth gate, beside edition (`useFeature`), entitlement (`useEntitlement`) and
 * permission (`usePermission`). It only ever narrows: a beta inside a paid module is
 * `useEntitlement(module) && useRollout(flag)`, never either alone. Unlike an
 * unentitled feature, a beta that is off is hidden outright rather than lock-badged —
 * there is nothing for the customer to buy or ask for yet.
 *
 * Reads the payload `useAuth` already holds, so this costs no fetch. Absent means
 * false, and the trial build is always false.
 */
export function useRollout(flag: RolloutFlag): boolean {
  return useRollouts()(flag);
}

/** A predicate over every flag, for a component that checks several (nav rendering). */
export function useRollouts(): (flag: RolloutFlag) => boolean {
  const { user } = useAuth();
  return (flag: RolloutFlag) => user?.rollouts?.[flag] ?? false;
}
